import { publicWildcardIsEnvironmentControlled } from "../../shared/publicUrl";
import {
  tenantApiOrigin,
  tenantDomainFromHost,
  tenantUiOriginForStack,
} from "../../shared/tenantResolution";
import { config } from "../environment";
import {
  derivePublicDomain,
  type PublicDomain,
} from "../hooks/derivePublicDomain";

export { tenantApiOrigin };

// The tenant's public origin is always dot-joined and always https — the
// tenant host is a single label under the base, so even preview's one-label
// TLS wildcard covers it.
export function tenantPublicOrigin(
  stackPublicBaseDomain: string,
  tenantPublicDomain: string
): string {
  return `https://${tenantPublicDomain}.${stackPublicBaseDomain}`;
}

// The tenant domain (the tenants.public_domain value) carried by the host
// the browser loaded, read with the same shared parser the server applies
// to the Host header — no session or Zero query needed. On a base-domain
// stack that's the label under {STACK_BASE_DOMAIN}; on a bare-host stack
// (development) the location hostname is the stored value itself. Null on
// single-tenant stacks and on hosts that name no tenant.
export function locationTenantDomain(): string | null {
  if (config.selfHostedSingleTenant) return null;
  return tenantDomainFromHost(window.location.hostname, config.stackBaseDomain);
}

// A configured URL re-rooted on the current tenant, read fresh from
// location on every call. Single-tenant stacks use the configured value
// as-is. Multitenant stacks compose the tenant origin from the location's
// tenant domain with the same helper the server uses (the configured value
// names the seeded tenant, never the current one); a host that names no
// tenant is a broken deployment, not a fallback.
function locationTenantOrigin(configuredUrl: string, caller: string): string {
  if (config.selfHostedSingleTenant) {
    return configuredUrl;
  }
  const tenantDomain = locationTenantDomain();
  if (!tenantDomain) {
    throw new Error(
      `${caller}: ${window.location.hostname} is not a tenant host under ${config.stackBaseDomain}`
    );
  }
  return tenantUiOriginForStack(
    configuredUrl,
    config.stackBaseDomain,
    tenantDomain
  );
}

// The origin the SPA addresses the API on (the get-session bootstrap, SSO
// start, the native callback, and every other UI call site alike). API
// traffic rides the app origin's path surfaces (/ui-api for the data plane,
// /sso, /oauth, /scim, … for registered URLs), so this is the app origin.
export function apiUrl(): string {
  return locationTenantOrigin(config.externalUiUrl!, "apiUrl");
}

// The origin external parties validated URLs against before the app-origin
// cutover: api.{label}.{STACK_BASE_DOMAIN} when a base is set; otherwise
// EXTERNAL_API_URL (required — mirrors tenantLegacyIdentityOrigin on the
// server; do not fall back to EXTERNAL_UI_URL).
function legacyIdentityOrigin(): string {
  if (config.selfHostedSingleTenant || !config.stackBaseDomain) {
    return config.externalApiUrl!;
  }
  const tenantDomain = locationTenantDomain();
  if (!tenantDomain) {
    throw new Error(
      `legacyIdentityOrigin: ${window.location.hostname} is not a tenant host under ${config.stackBaseDomain}`
    );
  }
  return tenantApiOrigin(config.stackBaseDomain, tenantDomain);
}

// Connector OAuth redirect URIs are outbound-validated: the provider
// exact-matches them against the customer's registered URIs, so legacy stacks
// keep the pre-cutover spelling (/connected-apps on the api origin) that
// existing registrations pin, mirroring getRedirectURI in
// services/api/connectors/templates.ts. Everything the UI displays or sends
// for connector OAuth builds on this base.
export function connectorOauthRedirectBase(): string {
  if (!config.legacyApiSubdomain) {
    return `${apiUrl()}/connectors/oauth/`;
  }
  return `${legacyIdentityOrigin()}/connected-apps/oauth/`;
}

export function connectorOauthRedirectUri(identifier: string): string {
  return `${connectorOauthRedirectBase()}${encodeURIComponent(identifier)}/callback`;
}

// The OIDC provider URL must byte-match the issuer advertised by discovery and
// minted by the proxy.
export function oidcProviderUrl(): string {
  return config.legacyApiSubdomain ? legacyIdentityOrigin() : apiUrl();
}

// Zero sync rides the app origin's /sync path surface on base-domain
// stacks, and zero-cache's own port in development — in both cases on the
// location's tenant host, which is what sends the tenant's host-only
// session cookie along for zero-cache to forward (cookies ignore ports).
export function zeroSyncUrl(): string {
  return locationTenantOrigin(config.zeroSyncUrl, "zeroSyncUrl");
}

// The tenant's externally reachable public origin, read the same way as
// apiUrl: the admin app and its API share a host, so the tenant domain in
// the URL bar is the same label tenants.public_domain carries.
// Single-tenant stacks and stacks with no public base domain use the
// configured URL as-is.
export function publicUrl(): string {
  if (
    publicWildcardIsEnvironmentControlled(
      config.maxTenants,
      config.combinedSpaceTenantHosts
    ) ||
    !config.stackPublicBaseDomain
  ) {
    return config.externalPublicUrl!;
  }
  const tenantDomain = locationTenantDomain();
  if (!tenantDomain) {
    throw new Error(
      `publicUrl: ${window.location.hostname} is not a tenant host under ${config.stackBaseDomain}`
    );
  }
  return tenantPublicOrigin(config.stackPublicBaseDomain, tenantDomain);
}

// The wildcard/suffix shape used to build page, step, and email URLs for the
// current tenant, read the same way as apiUrl/publicUrl: no query, so there
// is no loading state where a caller could observe the seeded tenant's
// domain while viewing a different one.
export function publicDomain(): PublicDomain {
  return derivePublicDomain(
    config.publicWildcardDomain,
    config.stackPublicBaseDomain,
    config.maxTenants,
    config.combinedSpaceTenantHosts,
    locationTenantDomain() ?? undefined
  );
}
